/**
 * Screen-share quality math (DOM-free, so it's unit-testable).
 *
 * "It's blurry and laggy" is not actionable; these numbers are. The two that matter most:
 *  - `width`/`height` say WHICH simulcast rung the SFU is actually forwarding. A viewer who asked
 *    for Sharp but is receiving 640x360 is being demoted upstream — a bandwidth problem, not an
 *    encoder-settings problem.
 *  - `meanQp` is the literal numeric definition of blur: the encoder's quantizer. Low is crisp;
 *    high means it ran out of bits and threw away detail. (H.264 QP is 0-51; over ~35 text mushes.)
 * `jitterMs` and `freezes` separate "the picture is soft" from "the picture is late".
 */

/** Raw cumulative counters straight off one `inbound-rtp` (video) stats entry. */
export interface RtpSnapshot {
  ts: number;                       // ms
  bytesReceived: number;
  framesDecoded: number;
  qpSum: number;
  jitterBufferDelay: number;        // seconds, cumulative
  jitterBufferEmittedCount: number;
  packetsReceived: number;
  packetsLost: number;
  freezeCount: number;
  pliCount: number;
  frameWidth: number;
  frameHeight: number;
  framesPerSecond: number;
  codec: string;
  decoder: string;
  rttMs: number | null;
}

export interface VideoStats {
  width: number;
  height: number;
  layer: 'Sharp' | 'Balanced' | 'Smooth' | '—'; // which rung is arriving (matches the picker's labels)
  fps: number;
  kbps: number;
  meanQp: number | null;
  jitterMs: number | null;
  lossPct: number;
  freezes: number;
  pli: number;
  codec: string;
  decoder: string;
  rttMs: number | null;
}

/**
 * Name the rung that's arriving. The rungs are FRACTIONS of the capture (the top one IS the capture;
 * the lower ones scale down from it), so this must be judged relative to the host's capture width —
 * not against absolute pixel bands. A shared window can be far narrower than a full screen, and
 * absolute bands would tell such a viewer they'd been demoted to "Smooth" while they were in fact
 * receiving the best layer that exists.
 */
export function layerFor(width: number, captureWidth = 0): VideoStats['layer'] {
  if (width <= 0) return '—';
  if (captureWidth <= 0) return '—';           // can't judge a rung without knowing the top one
  // Receiving MORE than the advertised capture means our reference is stale (the host swapped the
  // shared source live, which doesn't re-publish the track). Say nothing rather than say something
  // wrong — the raw resolution above it is still true.
  if (width > captureWidth) return '—';
  const ratio = width / captureWidth;
  if (ratio >= 0.9) return 'Sharp';            // scaleResolutionDownBy 1
  if (ratio >= 0.4) return 'Balanced';         // ~1/2
  return 'Smooth';                             // ~1/4
}

/**
 * Turn two cumulative snapshots into rates. Counters only ever grow, so a negative delta means the
 * track was replaced (a new share) — treat that as "no reading yet" rather than emitting nonsense.
 */
export function deriveStats(cur: RtpSnapshot, prev: RtpSnapshot | null, captureWidth = 0): VideoStats {
  const dt = prev ? (cur.ts - prev.ts) / 1000 : 0;
  const usable = !!prev && dt > 0 && cur.bytesReceived >= prev!.bytesReceived;

  const dBytes = usable ? cur.bytesReceived - prev!.bytesReceived : 0;
  const dFrames = usable ? cur.framesDecoded - prev!.framesDecoded : 0;
  const dQp = usable ? cur.qpSum - prev!.qpSum : 0;
  const dJitterDelay = usable ? cur.jitterBufferDelay - prev!.jitterBufferDelay : 0;
  const dEmitted = usable ? cur.jitterBufferEmittedCount - prev!.jitterBufferEmittedCount : 0;
  const dLost = usable ? cur.packetsLost - prev!.packetsLost : 0;
  const dRecv = usable ? cur.packetsReceived - prev!.packetsReceived : 0;

  const denom = dLost + dRecv;
  return {
    width: cur.frameWidth,
    height: cur.frameHeight,
    layer: layerFor(cur.frameWidth, captureWidth),
    fps: Math.round(cur.framesPerSecond || (usable && dt > 0 ? dFrames / dt : 0)),
    kbps: usable ? Math.round((dBytes * 8) / dt / 1000) : 0,
    meanQp: dFrames > 0 && dQp > 0 ? Math.round(dQp / dFrames) : null,
    jitterMs: dEmitted > 0 ? Math.round((dJitterDelay / dEmitted) * 1000) : null,
    lossPct: denom > 0 ? Math.max(0, Math.round((dLost / denom) * 1000) / 10) : 0,
    freezes: cur.freezeCount,
    pli: cur.pliCount,
    codec: cur.codec,
    decoder: cur.decoder,
    rttMs: cur.rttMs,
  };
}
